import PropTypes from 'prop-types'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { ToggleActive } from '@components/buttons'
import { CurrencyInput } from '@components/currency-input'
import { InfoPopover } from '@components/popover'
import {
  AdasBox,
  AdasButton,
  AdasButtonGroup,
  AdasCard,
  AdasFormControl,
  AdasInputLabel,
  AdasSelect,
  AdasTextField,
  AdasTypography
} from '@components/wrapper-components'

import {
  saveCalibration,
  saveService,
  saveLabour,
  saveParts,
  getProductCategories,
  updateCalibration,
  updateService,
  updateParts,
  updateLabour
} from '../../../api/api'

import { useUserStore } from '../../../store'
import { useCenterStore } from '@caradasstore/CenterStore'
import { validations, formatInitialPrice } from '../../../utils/utils'
import { Logger } from '../../../logger'

const prettyTypeName = {
  calibration: 'Calibration',
  service: 'Service',
  part: 'Part',
  labor: 'Labor'
}

const saveHandlers = {
  calibration: saveCalibration,
  service: saveService,
  part: saveParts,
  labor: saveLabour
}
const updateHandlers = {
  calibration: updateCalibration,
  service: updateService,
  part: updateParts,
  labor: updateLabour
}

export const ProductForm = ({ productData, close, onSaveSuccess, productTypeName, edit, multiline = true, selectedReferralId }) => {
  const ccId = useCenterStore((store) => store.ccId)
  const currentUser = useUserStore((store) => store.currentUser)
  const isAdmin = currentUser.is_admin
  const isOwner = currentUser.is_owner
  const [productCategories, setProductCategories] = useState([])
  const [touched, setTouched] = useState({ name: false, price: false, category: false })
  const [infoLabel] = useState(`A Default ${prettyTypeName[productTypeName]} is automatically included in all new Quotes & Work Orders`)
  const [isCategoryDisabled, setIsCategoryDisabled] = useState(false)

  const [product, setProduct] = useState({
    is_active: true,
    quantity: productData.quantity || 1,
    category_id: productData?.category?.id || '',
    ...productData
  })

  const [valid, setValid] = useState({
    name: validations.name(productData.name),
    price: validations.price(productData.price),
    category: productTypeName === 'calibration' ? validations.category(productData?.category?.id) : true
  })

  const fetchProductCategories = useCallback(async () => {
    try {
      const response = await getProductCategories({ ccid: ccId, type: productTypeName.toUpperCase() })
      if (response.status === 200) {
        const categories = response.data
        setProductCategories(categories)

        if (categories.length === 1 && productTypeName !== 'calibration') {
          const singleCategory = categories[0]
          setProduct((prev) => ({
            ...prev,
            category_id: singleCategory.id
          }))
          setValid((prev) => ({ ...prev, category: true }))
          setTouched((prev) => ({ ...prev, category: true }))
          setIsCategoryDisabled(true)
        }
      }
    } catch (error) {
      Logger.error({ message: error, payload: { file: 'ProductForm/fetchProductCategories' } })
    }
  }, [ccId, productTypeName])

  useEffect(() => {
    fetchProductCategories()
  }, [fetchProductCategories])

  const handleInputChange = useCallback(
    (e) => {
      const { name, value } = e.target

      if (name === 'category') {
        const selectedCategory = productCategories.find((cat) => cat.id === value)
        const validCategory = validations.category(selectedCategory?.id)

        setProduct((prev) => ({ ...prev, category_id: selectedCategory?.id || '' }))
        setTouched((prev) => ({ ...prev, category: true }))
        setValid((prev) => ({ ...prev, category: validCategory }))
        return
      }

      const sanitizedValue = name === 'quantity' ? parseInt(value.replace(/\D/g, ''), 10) || 0 : value

      setProduct((prev) => ({ ...prev, [name]: sanitizedValue }))
      setTouched((prev) => ({ ...prev, [name]: true }))
      setValid((prev) => ({
        ...prev,
        [name]: validations[name] ? validations[name](sanitizedValue) : true
      }))
    },
    [productCategories]
  )

  const handleToggleActive = (e) => {
    const isActive = e.target.value === 'active'
    setProduct((prev) => ({
      ...prev,
      is_active: isActive,
      is_defaulted: isActive ? prev.is_active : false
    }))
  }

  const handleToggleDefault = (e) => {
    setProduct((prev) => ({
      ...prev,
      is_defaulted: e.target.value === 'Yes'
    }))
  }
  const getPayload = () => {
    const baseData = {
      ...product,
      product_type: productTypeName.toUpperCase(),
      belongsTo: selectedReferralId ? 'REFERRAL' : 'CALIBRATION_CENTER',
      belongs_to_id: selectedReferralId ? selectedReferralId : ccId,
      calibration_center_id: productData.calibration_center_id || ccId
      // ...(selectedReferralId && { belongsTo: 'REFERRAL' })
    }
    return baseData
  }
  const handleSave = async () => {
    const actionHandlers = edit ? updateHandlers : saveHandlers
    const actionFunc = actionHandlers[productTypeName]
    if (!actionFunc) {
      Logger.error({ message: `Unknown product type: ${productTypeName}` })
      return
    }
    const data = getPayload()

    try {
      const response = await actionFunc({ data })
      if (response.status === 200) {
        close()
        onSaveSuccess(product)
      }
    } catch (error) {
      Logger.error({ message: error, payload: { file: 'ProductForm/handleSave', product } })
    }
  }

  const isSaveDisabled = useMemo(() => !valid.name || !valid.price || !valid.category, [valid])
  const priceLabel = productTypeName === 'labor' ? 'Hourly Rate *' : 'Price *'
  const quantityLabel = productTypeName === 'labor' ? 'Hours' : 'Quantity'

  return (
    <AdasCard className='adas-card'>
      <AdasBox sx={{ paddingBottom: '16px' }}>
        <AdasTypography variant='overline'>
          {productTypeName} ID #{product.itemNum}
        </AdasTypography>
      </AdasBox>

      <AdasFormControl fullWidth sx={{ paddingBottom: '1.5rem' }}>
        <AdasInputLabel id='category_label'>Category</AdasInputLabel>
        <AdasSelect
          labelId='Category'
          id='category'
          name='category'
          value={product.category_id}
          label='Category'
          onChange={handleInputChange}
          disabled={isCategoryDisabled || (edit && !isAdmin && productTypeName === 'calibration')}
          options={productCategories}
          error={touched.category && !valid.category}
        />
      </AdasFormControl>

      <AdasTextField
        onChange={handleInputChange}
        disabled={edit && !isAdmin && !isOwner}
        name='name'
        id='name'
        label='Name *'
        variant='outlined'
        value={product.name || ''}
        error={!valid.name && touched.name}
        fullWidth
        inputProps={{ maxLength: 80 }}
        InputLabelProps={{ shrink: true }}
        sx={{ marginBottom: '20px' }}
      />

      <CurrencyInput
        value={formatInitialPrice(product.price)} // update value depending on API
        error={touched.price && !valid.price}
        label={priceLabel}
        id='price'
        name='price'
        onChange={handleInputChange}
        fullWidth
        variant='outlined'
        InputLabelProps={{ shrink: true }}
        sx={{ marginBottom: '20px' }}
      />

      <AdasTextField
        onChange={handleInputChange}
        name='quantity'
        id='quantity'
        label={quantityLabel}
        variant='outlined'
        value={product.quantity || ''}
        // disabled={edit || productTypeName === 'labor'} // Uncomment if you want to disable quantity field in edit mode or for labor type
        disabled={true}
        fullWidth
        inputProps={{ maxLength: 3, inputMode: 'numeric', pattern: '\\d*' }}
        InputLabelProps={{ shrink: true }}
        sx={{ marginBottom: '20px' }}
      />

      <AdasTextField
        onChange={handleInputChange}
        name='description'
        id='description'
        label='Description'
        variant='outlined'
        multiline={multiline}
        value={product.description || ''}
        fullWidth
        inputProps={{ maxLength: 100 }}
        InputLabelProps={{ shrink: true }}
        sx={{ marginBottom: '20px' }}
      />

      <ToggleActive toggle={product.is_active} handleToggleActive={handleToggleActive} />

      <>
        <AdasInputLabel htmlFor='addToAll'>Default {prettyTypeName[productTypeName]}</AdasInputLabel>
        <AdasButtonGroup
          type='toggleButton'
          value={product.is_defaulted ? 'Yes' : 'No'}
          exclusive
          onChange={handleToggleDefault}
          name='addToAll'
          sx={{ marginBottom: '20px' }}
        >
          <AdasButton buttonType='toggleButton' value='Yes' color='primary'>
            Yes
          </AdasButton>
          <AdasButton buttonType='toggleButton' value='No' color='primary'>
            No
          </AdasButton>
        </AdasButtonGroup>
        <InfoPopover text={infoLabel} ariaLabel='Information on default' />
      </>

      <AdasBox sx={{ margin: '20px 0px' }}>
        <AdasButton variant='outlined' onClick={close} color='primary'>
          Cancel
        </AdasButton>
        <AdasButton
          type='submit'
          disabled={isSaveDisabled}
          sx={{ float: 'right' }}
          variant='contained'
          color='primary'
          onClick={handleSave}
          isDebounceEnabled={true}
        >
          Save
        </AdasButton>
      </AdasBox>
    </AdasCard>
  )
}

ProductForm.propTypes = {
  productData: PropTypes.object.isRequired,
  close: PropTypes.func.isRequired,
  onSaveSuccess: PropTypes.func.isRequired,
  productTypeName: PropTypes.string.isRequired,
  edit: PropTypes.bool,
  multiline: PropTypes.bool
}
